import React, { useEffect, useState } from 'react';
import { getAds, saveAd, deleteAd } from '../services/api';
import { PlusCircle, Trash, ExternalLink } from 'lucide-react';

const ManageAds = () => {
  const [ads, setAds] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({ title: '', link: '', image: '', placement: 'sidebar', status: 'active' });

  const loadAds = async () => {
    const data = await getAds();
    setAds(data);
  };

  useEffect(() => {
    loadAds();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await saveAd(formData);
    setFormData({ title: '', link: '', image: '', placement: 'sidebar', status: 'active' });
    setIsCreating(false);
    loadAds();
  };

  const handleDelete = async (id) => {
    if(window.confirm('Delete this ad?')) {
      await deleteAd(id);
      loadAds();
    }
  };

  return (
    <div>
      <div className="flex justify-between items-end mb-8 pt-2">
        <div>
          <h1 className="text-3xl font-extrabold text-stone-900 mb-2">Advertisement Campaigns</h1>
          <p className="text-stone-500">Configure global ad banners across frontend placements.</p>
        </div>
        <button 
          onClick={() => setIsCreating(!isCreating)}
          className="inline-flex items-center px-4 py-2.5 bg-saffron-600 text-white font-medium rounded-xl hover:bg-saffron-700 transition"
        >
          <PlusCircle className="w-5 h-5 mr-2" /> New Ad
        </button>
      </div>

      {isCreating && (
        <div className="bg-stone-50 rounded-2xl border border-stone-200 p-6 mb-8 border-l-4 border-l-saffron-500">
          <h2 className="text-lg font-bold text-stone-800 mb-4">Create Advertisement</h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-stone-700 mb-1">Company / Title</label>
              <input required value={formData.title} onChange={e=>setFormData({...formData, title: e.target.value})} className="w-full border border-stone-300 rounded-lg p-2.5" placeholder="Spritual Books Pvt." />
            </div>
            <div>
              <label className="block text-sm font-semibold text-stone-700 mb-1">Target Link</label>
              <input required type="url" value={formData.link} onChange={e=>setFormData({...formData, link: e.target.value})} className="w-full border border-stone-300 rounded-lg p-2.5" placeholder="https://..." />
            </div>
            <div>
              <label className="block text-sm font-semibold text-stone-700 mb-1">Banner Image URL or Upload</label>
              <div className="flex flex-col space-y-2">
                <input type="url" value={formData.image.startsWith('data:') ? '' : formData.image} onChange={e=>setFormData({...formData, image: e.target.value})} className="w-full border border-stone-300 rounded-lg p-2.5" placeholder="Paste URL..." required={!formData.image} />
                <input 
                  type="file" 
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onloadend = () => setFormData({...formData, image: reader.result});
                      reader.readAsDataURL(file);
                    }
                  }}
                  className="w-full text-sm text-stone-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-stone-100 file:text-stone-700 hover:file:bg-stone-200 cursor-pointer"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-1">Placement</label>
                <select value={formData.placement} onChange={e=>setFormData({...formData, placement: e.target.value})} className="w-full border border-stone-300 rounded-lg p-2.5 bg-white">
                  <option value="sidebar">Right Sidebar</option>
                  <option value="banner">Top Banner</option>
                  <option value="in-content">Article Body</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-1">Status</label>
                <select value={formData.status} onChange={e=>setFormData({...formData, status: e.target.value})} className="w-full border border-stone-300 rounded-lg p-2.5 bg-white">
                  <option value="active">Active</option>
                  <option value="inactive">Paused</option>
                </select>
              </div>
            </div>
            <div className="md:col-span-2 flex justify-end space-x-3 mt-4">
              <button type="button" onClick={() => setIsCreating(false)} className="px-6 py-2.5 text-stone-600 hover:bg-stone-200 bg-stone-100 rounded-xl font-medium transition">Cancel</button>
              <button type="submit" className="px-6 py-2.5 text-white bg-saffron-600 hover:bg-saffron-700 rounded-xl font-medium transition">Save Campaign</button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {ads.map(ad => (
          <div key={ad.id} className="border border-stone-200 rounded-2xl overflow-hidden bg-white shadow-sm flex flex-col group">
             <div className="h-40 bg-stone-100 overflow-hidden relative border-b border-stone-100">
                <img src={ad.image} alt={ad.title} className="w-full h-full object-cover" />
                <span className={`absolute top-2 right-2 px-2.5 py-1 text-[10px] font-bold uppercase rounded-full tracking-wider ${ad.status === 'active' ? 'bg-green-500 text-white' : 'bg-stone-500 text-white'}`}>
                  {ad.status}
                </span>
             </div>
             <div className="p-5 flex-1 flex flex-col">
               <span className="text-xs font-bold text-saffron-600 uppercase tracking-widest mb-1">{ad.placement} placement</span>
               <h3 className="font-extrabold text-stone-900 text-lg mb-4">{ad.title}</h3>
               
               <div className="mt-auto flex justify-between items-center border-t border-stone-100 pt-4">
                 <a href={ad.link} target="_blank" rel="noreferrer" className="text-stone-500 hover:text-blue-600 flex items-center text-sm font-semibold">
                   <ExternalLink className="w-4 h-4 mr-1"/> Test Link
                 </a>
                 <button onClick={() => handleDelete(ad.id)} className="text-red-400 hover:text-red-600 p-2 rounded-lg hover:bg-red-50 transition">
                   <Trash className="w-5 h-5"/>
                 </button>
               </div>
             </div>
          </div>
        ))}
      </div>
      
    </div>
  );
};

export default ManageAds;
